import { SelectOption } from '@globaleris/ui.library'
import {
  apiGetPostalAddressByPostalCode,
  apiGetPostalCodes
} from '../../api/Argentia/endpoints/addresses/addresses.api'
import { apiFetchPaymentMethods } from '../../api/Argentia/endpoints/contracts/contracts.api'
import {
  ChildSettlementGroupModel,
  ChildSettlementGroupRequestModelDTO,
  NewSettlementGroupContractModel,
  NewSettlementGroupSubjectModel,
  PatchSettlementGroupContractModel,
  PatchSettlementGroupSubjectModel,
  SettlementGroup,
  SettlementGroupAggregatedEditBatchRequest,
  SettlementGroupContractModel,
  SettlementGroupContractMoveRequest,
  SettlementGroupPatchRequest,
  SettlementGroupPostRequest
} from '../../api/Argentia/endpoints/settlementGroups/model/settlementGroups'
import {
  apiCreateChildSettlementGroup,
  apiDeleteChildSettlementGroup,
  apiDeleteSettlementGroup,
  apiDeleteSettlementGroupContract,
  apiDeleteSettlementGroupSubject,
  apiFetchCommoditiesForSettlementGroup,
  apiFetchContractsBySgId,
  apiGetChildSettlementGroups,
  apiGetParentSettlementGroupOptions,
  apiGetSettlementGroupsBusinessGroup,
  apiGetSettlementGroupsVirtualBattery,
  apiGetSettlementGroupsWithContracts,
  apiGetSettlementGroupsWithFlexibilities,
  apiMoveSettlementGroupContract,
  apiPatchSettlementGroupContract,
  apiPatchSettlementGroups,
  apiPatchSettlementGroupSubjectList,
  apiPostSettlementGroupContract,
  apiPostSettlementGroups,
  apiPostSettlementGroupSubjectList,
  apiUpdateChildSettlementGroup,
  apiUpdateSettlementGroupBatch
} from '../../api/Argentia/endpoints/settlementGroups/settlementGroups.api'
import { EnumCommodity } from '../../api/Argentia/enums'
import { PostalAddress } from '../../api/Argentia/model/addresses'
import { PaymentMethodTypeModel } from '../../api/Argentia/model/contracts'
import {
  createErrorServiceResponse,
  createSuccessServiceResponse
} from '../../helpers/serviceResponseHelper'
import i18n from '../../i18n'
import { ServiceResponse } from '../../model/serviceResponse'
import { notifyError } from '../../redux'
import { postalCodeListMapper } from './settlementGroups.mapper'

export const getSettlementGroupsWithFlexibilities = async (): Promise<
  ServiceResponse<SettlementGroup[] | undefined>
> => {
  return await apiGetSettlementGroupsWithFlexibilities()
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      notifyError(i18n.t('settlementGroups:notifications.getAll.error'))
      return createErrorServiceResponse(error)
    })
}

export const getSettlementGroupsWithContracts = async (): Promise<
  ServiceResponse<SettlementGroup[] | undefined>
> => {
  return await apiGetSettlementGroupsWithContracts()
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      notifyError(i18n.t('settlementGroups:notifications.getAll.error'))
      return createErrorServiceResponse(error)
    })
}

export const getSettlementGroupsVirtualBattery = async (): Promise<
  ServiceResponse<SettlementGroup[] | undefined>
> => {
  return await apiGetSettlementGroupsVirtualBattery()
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      notifyError(i18n.t('settlementGroups:notifications.getAll.error'))
      return createErrorServiceResponse(error)
    })
}

export const getSettlementGroupsBusinessGroup = async (): Promise<
  ServiceResponse<SettlementGroup[] | undefined>
> => {
  return await apiGetSettlementGroupsBusinessGroup()
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      notifyError(i18n.t('settlementGroups:notifications.getAll.error'))
      return createErrorServiceResponse(error)
    })
}

export const getParentSettlementGroupOptions = async (): Promise<
  ServiceResponse<SelectOption[] | undefined>
> => {
  return await apiGetParentSettlementGroupOptions()
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      notifyError(i18n.t('settlementGroups:notifications.getAll.error'))
      return createErrorServiceResponse(error)
    })
}

export const getChildSettlementGroups = async (
  parentId: number
): Promise<ServiceResponse<ChildSettlementGroupModel[] | undefined>> => {
  return await apiGetChildSettlementGroups(parentId)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      notifyError(i18n.t('settlementGroups:notifications.getAll.error'))
      return createErrorServiceResponse(error)
    })
}

export const createChildSettlementGroup = async (
  request: ChildSettlementGroupRequestModelDTO
): Promise<ServiceResponse<ChildSettlementGroupModel | undefined>> => {
  return await apiCreateChildSettlementGroup(request)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      notifyError(i18n.t('settlementGroups:notifications.create.error'))
      return createErrorServiceResponse(error)
    })
}

export const updateChildSettlementGroup = async (
  request: ChildSettlementGroupRequestModelDTO
): Promise<ServiceResponse<ChildSettlementGroupModel | undefined>> => {
  return await apiUpdateChildSettlementGroup(request)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      notifyError(i18n.t('settlementGroups:notifications.update.error'))
      return createErrorServiceResponse(error)
    })
}

export const deleteChildSettlementGroup = async (
  parentId: number,
  childId: number
): Promise<ServiceResponse<any>> => {
  return await apiDeleteChildSettlementGroup(parentId, childId)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      notifyError(i18n.t('settlementGroups:notifications.delete.error'))
      return createErrorServiceResponse(error)
    })
}

export const createSettlementGroup = async (
  request: SettlementGroupPostRequest
): Promise<ServiceResponse<SettlementGroup | undefined>> => {
  return await apiPostSettlementGroups(request)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      notifyError(
        error?.statusCode === 409
          ? i18n.t('settlementGroups:notifications.create.duplicatedCode', { code: request.code })
          : i18n.t('settlementGroups:notifications.create.error')
      )
      return createErrorServiceResponse(error)
    })
}

export const patchSettlementGroup = async (
  request: SettlementGroupPatchRequest
): Promise<ServiceResponse<SettlementGroup | undefined>> => {
  return await apiPatchSettlementGroups(request)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      notifyError(i18n.t('settlementGroups:notifications.update.error'))
      return createErrorServiceResponse(error)
    })
}

export const isSettlementGroupCodeDuplicated = (
  code: string | undefined,
  settlementGroups: { id: number; code: string }[],
  id?: number
): boolean => {
  const normalizedCode = code?.trim().toUpperCase()
  if (!normalizedCode) return false

  return settlementGroups.some(
    group => group.id !== id && group.code?.trim().toUpperCase() === normalizedCode
  )
}

export const updateSettlementGroupBatch = async (
  request: SettlementGroupAggregatedEditBatchRequest
): Promise<ServiceResponse<any>> => {
  return await apiUpdateSettlementGroupBatch(request)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      notifyError(i18n.t('settlementGroups:notifications.create.error'))
      return createErrorServiceResponse(error)
    })
}

export const deleteSettlementGroup = async (settlementGroupId: number) => {
  return await apiDeleteSettlementGroup(settlementGroupId)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchSettlementGroupCommodity = async (
  settlementGroupId: number
): Promise<ServiceResponse<EnumCommodity | null | undefined>> => {
  return await apiFetchCommoditiesForSettlementGroup(settlementGroupId)
    .then(res => {
      return createSuccessServiceResponse(
        res && res.length > 0 && res[0] > 0 && res.every(x => x === res[0]) ? res[0] : 1
      )
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchPostalCodes = async (query: string): Promise<SelectOption[]> => {
  return await apiGetPostalCodes(query).then(response => {
    return postalCodeListMapper(response)
  })
  // .catch(error => {
  //   return createErrorServiceResponse(error)
  // })
}

export const getPostalAddressByID = async (postalCodeId: number): Promise<PostalAddress> => {
  return await apiGetPostalAddressByPostalCode(postalCodeId).then(response => {
    return response
  })
  // .catch(error => {
  //   return createErrorServiceResponse(error)
  // })
}

export const fetchPaymentMethodsTypes = async (): Promise<
  ServiceResponse<PaymentMethodTypeModel[] | undefined>
> => {
  return await apiFetchPaymentMethods()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchContractsBySgId = async (
  settlementGroupId: number
): Promise<ServiceResponse<SettlementGroupContractModel[] | undefined>> => {
  return await apiFetchContractsBySgId(settlementGroupId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const createSettlementGroupContract = async (
  settlementGroupId: number,
  request: NewSettlementGroupContractModel[]
) => {
  return await apiPostSettlementGroupContract(settlementGroupId, request)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const patchSettlementGroupContract = async (
  settlementGroupId: number,
  id: number,
  request: PatchSettlementGroupContractModel
) => {
  return await apiPatchSettlementGroupContract(settlementGroupId, id, request)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const deleteSettlementGroupContract = async (settlementGroupId: number, id: number) => {
  return await apiDeleteSettlementGroupContract(settlementGroupId, id)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const moveSettlementGroupContract = async (request: SettlementGroupContractMoveRequest) => {
  return await apiMoveSettlementGroupContract(request)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const createSettlementGroupSubjectList = async (
  settlementGroupId: number,
  request: NewSettlementGroupSubjectModel[]
) => {
  return await apiPostSettlementGroupSubjectList(settlementGroupId, request)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const patchSettlementGroupSubjectList = async (
  settlementGroupId: number,
  id: number,
  request: PatchSettlementGroupSubjectModel
) => {
  return await apiPatchSettlementGroupSubjectList(settlementGroupId, id, request)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const deleteSettlementGroupSubject = async (settlementGroupId: number, id: number) => {
  return await apiDeleteSettlementGroupSubject(settlementGroupId, id)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}
